import React, { Component } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

class AdminLocationsPage extends Component {
  state = {
    // Current View: 'countries' | 'states' | 'cities'
    currentView: 'countries',

    // Data
    countries: [],
    states: [],
    cities: [],

    // Selection state
    selectedCountry: null,
    selectedState: null,

    // Pagination & Search
    page: 1,
    limit: 20,
    total: 0,
    search: "",

    loading: false,

    // Modals
    showCountryModal: false,
    countryFormIso: "",
    countryFormName: "",
    countryFormPrintableName: "",
    isEditCountry: false,

    showStateModal: false,
    stateFormId: "",
    stateFormName: "",

    showCityModal: false,
    cityFormId: "",
    cityFormName: "",
  };

  componentDidMount() {
    this.fetchCountries();
  }

  // ----- FETCHING DATA -----

  fetchCountries = async () => {
    this.setState({ loading: true, currentView: 'countries' });
    const { page, limit, search } = this.state;
    try {
      const res = await api.get(`/admin/settings/countries?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      if (res.data.success) {
        this.setState({ countries: res.data.data.data, total: res.data.data.total });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching countries");
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchStates = async (countryIso) => {
    this.setState({ loading: true, currentView: 'states' });
    const { page, limit, search } = this.state;
    try {
      const res = await api.get(`/admin/settings/states?country_iso=${countryIso}&page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      if (res.data.success) {
        this.setState({ states: res.data.data.data, total: res.data.data.total });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching states");
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchCities = async (stateId) => {
    this.setState({ loading: true, currentView: 'cities' });
    const { page, limit, search } = this.state;
    try {
      const res = await api.get(`/admin/settings/cities?state_id=${stateId}&page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      if (res.data.success) {
        this.setState({ cities: res.data.data.data, total: res.data.data.total });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching cities");
    } finally {
      this.setState({ loading: false });
    }
  };

  // ----- PAGINATION & SEARCH -----

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.setState({ page: 1 }, this.refreshCurrentView);
  };

  clearSearch = () => {
    this.setState({ search: "", page: 1 }, this.refreshCurrentView);
  };

  changePage = (newPage) => {
    this.setState({ page: newPage }, this.refreshCurrentView);
  };

  refreshCurrentView = () => {
    const { currentView, selectedCountry, selectedState } = this.state;
    if (currentView === 'countries') this.fetchCountries();
    else if (currentView === 'states') this.fetchStates(selectedCountry.iso);
    else if (currentView === 'cities') this.fetchCities(selectedState.id);
  };

  // ----- NAVIGATION -----

  viewStates = (countryObj) => {
    this.setState({ selectedCountry: countryObj, page: 1, search: "" }, () => {
      this.fetchStates(countryObj.iso);
    });
  };

  viewCities = (stateObj) => {
    this.setState({ selectedState: stateObj, page: 1, search: "" }, () => {
      this.fetchCities(stateObj.id);
    });
  };

  backToCountries = () => {
    this.setState({ selectedCountry: null, selectedState: null, page: 1, search: "" }, () => {
      this.fetchCountries();
    });
  };

  backToStates = () => {
    this.setState({ selectedState: null, page: 1, search: "" }, () => {
      this.fetchStates(this.state.selectedCountry.iso);
    });
  };

  // ----- COUNTRY HANDLERS -----

  openCountryModal = (countryObj = null) => {
    if (countryObj) {
      this.setState({
        showCountryModal: true,
        countryFormIso: countryObj.iso,
        countryFormName: countryObj.name,
        countryFormPrintableName: countryObj.printable_name || "",
        isEditCountry: true,
      });
    } else {
      this.setState({
        showCountryModal: true,
        countryFormIso: "",
        countryFormName: "",
        countryFormPrintableName: "",
        isEditCountry: false,
      });
    }
  };

  closeCountryModal = () => this.setState({ showCountryModal: false });

  saveCountry = async (e) => {
    e.preventDefault();
    const { countryFormIso, countryFormName, countryFormPrintableName, isEditCountry } = this.state;
    if (!countryFormIso || !countryFormName) {
      return toast.error("ISO and Name are required");
    }
    try {
      const res = await api.post("/admin/settings/country", {
        iso: countryFormIso,
        name: countryFormName,
        printable_name: countryFormPrintableName,
        isEdit: isEditCountry
      });
      if (res.data.success) {
        toast.success(isEditCountry ? "Country updated" : "Country added");
        this.closeCountryModal();
        this.fetchCountries();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving country");
    }
  };

  toggleCountryStatus = async (countryObj) => {
    const newStatus = countryObj.active === 1 ? 0 : 1;
    try {
      const res = await api.post("/admin/settings/country/status", { id: countryObj.iso, active: newStatus });
      if (res.data.success) {
        toast.success(`Country ${newStatus === 1 ? "Activated" : "Deactivated"}`);
        this.fetchCountries();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating country status");
    }
  };

  // ----- STATE HANDLERS -----

  openStateModal = (stateObj = null) => {
    if (stateObj) {
      this.setState({ showStateModal: true, stateFormId: stateObj.id, stateFormName: stateObj.name });
    } else {
      this.setState({ showStateModal: true, stateFormId: "", stateFormName: "" });
    }
  };

  closeStateModal = () => this.setState({ showStateModal: false });

  saveState = async (e) => {
    e.preventDefault();
    const { stateFormId, stateFormName, selectedCountry } = this.state;
    if (!stateFormName) return toast.error("State name is required");
    try {
      const res = await api.post("/admin/settings/state", { id: stateFormId, name: stateFormName, country_iso: selectedCountry.iso });
      if (res.data.success) {
        toast.success(stateFormId ? "State updated" : "State added");
        this.closeStateModal();
        this.fetchStates(selectedCountry.iso);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving state");
    }
  };

  toggleStateStatus = async (stateObj) => {
    const newStatus = stateObj.active === 1 ? 0 : 1;
    try {
      const res = await api.post("/admin/settings/state/status", { id: stateObj.id, active: newStatus });
      if (res.data.success) {
        toast.success(`State ${newStatus === 1 ? "Activated" : "Deactivated"}`);
        this.fetchStates(this.state.selectedCountry.iso);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating state status");
    }
  };

  // ----- CITY HANDLERS -----

  openCityModal = (cityObj = null) => {
    if (cityObj) {
      this.setState({ showCityModal: true, cityFormId: cityObj.id, cityFormName: cityObj.name });
    } else {
      this.setState({ showCityModal: true, cityFormId: "", cityFormName: "" });
    }
  };

  closeCityModal = () => this.setState({ showCityModal: false });

  saveCity = async (e) => {
    e.preventDefault();
    const { cityFormId, cityFormName, selectedState } = this.state;
    if (!cityFormName) return toast.error("City name is required");
    try {
      const res = await api.post("/admin/settings/city", { id: cityFormId, name: cityFormName, state_id: selectedState.id });
      if (res.data.success) {
        toast.success(cityFormId ? "City updated" : "City added");
        this.closeCityModal();
        this.fetchCities(selectedState.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving city");
    }
  };

  toggleCityStatus = async (cityObj) => {
    const newStatus = cityObj.active === 1 ? 0 : 1;
    try {
      const res = await api.post("/admin/settings/city/status", { id: cityObj.id, active: newStatus });
      if (res.data.success) {
        toast.success(`City ${newStatus === 1 ? "Activated" : "Deactivated"}`);
        this.fetchCities(this.state.selectedState.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating city status");
    }
  };

  renderPagination = () => {
    const { page, limit, total } = this.state;
    const totalPages = Math.ceil(total / limit) || 1;
    return (
      <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ marginTop: "14px", gap: "10px" }}>
        <small className="text-muted">
          Showing <strong>{total > 0 ? (page - 1) * limit + 1 : 0}</strong>–<strong>{Math.min(page * limit, total)}</strong> of <strong>{total}</strong> entries
        </small>
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <button
            className="adm-btn adm-btn-outline adm-btn-sm"
            disabled={page <= 1}
            onClick={() => page > 1 && this.changePage(page - 1)}
          >
            <i className="fas fa-chevron-left"></i>Prev
          </button>
          <span className="text-muted small">Page {page} of {totalPages}</span>
          <button
            className="adm-btn adm-btn-outline adm-btn-sm"
            disabled={page >= totalPages}
            onClick={() => page < totalPages && this.changePage(page + 1)}
          >
            Next<i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  renderSearchBar = () => (
    <div className="adm-filter-group grow">
      <label>Search</label>
      <div className="d-flex" style={{ gap: "8px" }}>
        <input
          type="text"
          style={{ flex: 1 }}
          placeholder="Search by name..."
          value={this.state.search}
          onChange={this.handleSearchChange}
        />
        <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={this.handleSearchSubmit}>
          <i className="fas fa-search"></i>Search
        </button>
        {this.state.search && (
          <button type="button" className="adm-btn adm-btn-outline adm-btn-sm" onClick={this.clearSearch}>
            <i className="fas fa-times"></i>Clear
          </button>
        )}
      </div>
    </div>
  );

  render() {
    const { currentView, countries, states, cities, loading, selectedCountry, selectedState } = this.state;

    return (
      <div className="adm-page">
        <div className="adm-page-inner">
          <div className="adm-header">
            <div>
              <h4 className="adm-header-title">
                <span className="adm-icon-badge"><i className="fas fa-globe-asia"></i></span>
                Locations Directory
              </h4>
              <p className="adm-header-sub">Manage countries, states, and cities with drill-down navigation.</p>
            </div>
            <div className="adm-toolbar">
              <span className="adm-badge adm-badge-primary text-capitalize">
                <i className="fas fa-layer-group"></i>{currentView}
              </span>
            </div>
          </div>

          {/* Countries */}
          {currentView === 'countries' && (
            <div className="adm-card">
              <div className="adm-card-body">
                <div className="adm-filter-bar" style={{ marginBottom: "18px" }}>
                  {this.renderSearchBar()}
                  <div className="adm-filter-group">
                    <label style={{ visibility: "hidden" }}>Add</label>
                    <button className="adm-btn adm-btn-primary" onClick={() => this.openCountryModal()}>
                      <i className="fas fa-plus"></i>Add Country
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="adm-spinner-row"><i className="fas fa-spinner fa-spin"></i></div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>ISO</th>
                          <th>Country Name</th>
                          <th>Printable Name</th>
                          <th>Status</th>
                          <th className="num">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countries.map(c => (
                          <tr key={c.iso}>
                            <td><span className="adm-badge adm-badge-primary">{c.iso}</span></td>
                            <td style={{ fontWeight: 700 }}>{c.name}</td>
                            <td style={{ color: "var(--adm-text-muted)" }}>{c.printable_name}</td>
                            <td>
                              <span className={`adm-badge ${c.active === 1 ? "adm-badge-success" : "adm-badge-muted"}`}>
                                {c.active === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="num">
                              <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.openCountryModal(c)}>
                                  <i className="fas fa-edit"></i>Edit
                                </button>
                                <button
                                  className={`adm-btn adm-btn-sm ${c.active === 1 ? "adm-btn-outline-danger" : "adm-btn-outline-success"}`}
                                  onClick={() => this.toggleCountryStatus(c)}
                                >
                                  <i className={`fas fa-${c.active === 1 ? "times" : "check"}`}></i>
                                  {c.active === 1 ? "Deactivate" : "Activate"}
                                </button>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.viewStates(c)}>
                                  States<i className="fas fa-chevron-right"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {countries.length === 0 && (
                      <div className="adm-empty">
                        <i className="fas fa-inbox"></i>
                        <strong>No countries found</strong>
                        <span>Add one to get started.</span>
                      </div>
                    )}
                  </div>
                )}
                {this.renderPagination()}
              </div>
            </div>
          )}

          {/* States */}
          {currentView === 'states' && (
            <div className="adm-card">
              <div className="adm-card-body">
                <div className="d-flex align-items-center" style={{ gap: "10px", marginBottom: "18px" }}>
                  <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={this.backToCountries}>
                    <i className="fas fa-arrow-left"></i>Back
                  </button>
                  <span className="text-muted small">
                    <strong style={{ color: "var(--adm-text)" }}>{selectedCountry?.name}'s States</strong>
                  </span>
                </div>
                <div className="adm-filter-bar" style={{ marginBottom: "18px" }}>
                  {this.renderSearchBar()}
                  <div className="adm-filter-group">
                    <label style={{ visibility: "hidden" }}>Add</label>
                    <button className="adm-btn adm-btn-primary" onClick={() => this.openStateModal()}>
                      <i className="fas fa-plus"></i>Add State
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="adm-spinner-row"><i className="fas fa-spinner fa-spin"></i></div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>State Name</th>
                          <th>Status</th>
                          <th className="num">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {states.map(s => (
                          <tr key={s.id}>
                            <td style={{ color: "var(--adm-text-muted)" }}>#{s.id}</td>
                            <td style={{ fontWeight: 700 }}>{s.name}</td>
                            <td>
                              <span className={`adm-badge ${s.active === 1 ? "adm-badge-success" : "adm-badge-muted"}`}>
                                {s.active === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="num">
                              <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.openStateModal(s)}>
                                  <i className="fas fa-edit"></i>Edit
                                </button>
                                <button
                                  className={`adm-btn adm-btn-sm ${s.active === 1 ? "adm-btn-outline-danger" : "adm-btn-outline-success"}`}
                                  onClick={() => this.toggleStateStatus(s)}
                                >
                                  <i className={`fas fa-${s.active === 1 ? "times" : "check"}`}></i>
                                  {s.active === 1 ? "Deactivate" : "Activate"}
                                </button>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.viewCities(s)}>
                                  Cities<i className="fas fa-chevron-right"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {states.length === 0 && (
                      <div className="adm-empty">
                        <i className="fas fa-inbox"></i>
                        <strong>No states found</strong>
                        <span>Add one to get started.</span>
                      </div>
                    )}
                  </div>
                )}

                {this.renderPagination()}
              </div>
            </div>
          )}

          {/* Cities */}
          {currentView === 'cities' && (
            <div className="adm-card">
              <div className="adm-card-body">
                <div className="d-flex align-items-center" style={{ gap: "10px", marginBottom: "18px" }}>
                  <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={this.backToStates}>
                    <i className="fas fa-arrow-left"></i>Back
                  </button>
                  <span className="text-muted small">
                    <strong style={{ color: "var(--adm-text)" }}>{selectedCountry?.name} &rarr; {selectedState?.name}'s Cities</strong>
                  </span>
                </div>
                <div className="adm-filter-bar" style={{ marginBottom: "18px" }}>
                  {this.renderSearchBar()}
                  <div className="adm-filter-group">
                    <label style={{ visibility: "hidden" }}>Add</label>
                    <button className="adm-btn adm-btn-primary" onClick={() => this.openCityModal()}>
                      <i className="fas fa-plus"></i>Add City
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="adm-spinner-row"><i className="fas fa-spinner fa-spin"></i></div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>City Name</th>
                          <th>Status</th>
                          <th className="num">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cities.map(c => (
                          <tr key={c.id}>
                            <td style={{ color: "var(--adm-text-muted)" }}>#{c.id}</td>
                            <td style={{ fontWeight: 700 }}>{c.name}</td>
                            <td>
                              <span className={`adm-badge ${c.active === 1 ? "adm-badge-success" : "adm-badge-muted"}`}>
                                {c.active === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="num">
                              <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.openCityModal(c)}>
                                  <i className="fas fa-edit"></i>Edit
                                </button>
                                <button
                                  className={`adm-btn adm-btn-sm ${c.active === 1 ? "adm-btn-outline-danger" : "adm-btn-outline-success"}`}
                                  onClick={() => this.toggleCityStatus(c)}
                                >
                                  <i className={`fas fa-${c.active === 1 ? "times" : "check"}`}></i>
                                  {c.active === 1 ? "Deactivate" : "Activate"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {cities.length === 0 && (
                      <div className="adm-empty">
                        <i className="fas fa-inbox"></i>
                        <strong>No cities found</strong>
                        <span>Add one to get started.</span>
                      </div>
                    )}
                  </div>
                )}

                {this.renderPagination()}
              </div>
            </div>
          )}
        </div>

        {/* Country Modal */}
        {this.state.showCountryModal && (
          <div className="adm-modal-overlay">
            <div className="adm-modal-panel">
              <div className="adm-modal-header">
                <h5>
                  <i className={`fas fa-${this.state.isEditCountry ? "edit" : "plus-circle"}`} style={{ color: "var(--adm-primary)" }}></i>
                  {this.state.isEditCountry ? "Edit Country" : "Add Country"}
                </h5>
                <button type="button" className="adm-close-btn" onClick={this.closeCountryModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={this.saveCountry}>
                <div className="adm-modal-body">
                  <div className="adm-filter-group" style={{ marginBottom: "14px" }}>
                    <label>ISO Code</label>
                    <input
                      type="text" required
                      disabled={this.state.isEditCountry} maxLength={2}
                      value={this.state.countryFormIso}
                      onChange={e => this.setState({ countryFormIso: e.target.value.toUpperCase() })}
                      placeholder="e.g. US"
                    />
                    <small className="text-muted">2-letter ISO code.</small>
                  </div>
                  <div className="adm-filter-group" style={{ marginBottom: "14px" }}>
                    <label>Country Name</label>
                    <input
                      type="text" required
                      value={this.state.countryFormName}
                      onChange={e => this.setState({ countryFormName: e.target.value })}
                      placeholder="Enter country name"
                    />
                  </div>
                  <div className="adm-filter-group">
                    <label>Printable Name</label>
                    <input
                      type="text"
                      value={this.state.countryFormPrintableName}
                      onChange={e => this.setState({ countryFormPrintableName: e.target.value })}
                      placeholder="Enter printable name"
                    />
                  </div>
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-outline" onClick={this.closeCountryModal}>Cancel</button>
                  <button type="submit" className="adm-btn adm-btn-primary">
                    <i className="fas fa-save"></i>Save Country
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* State Modal */}
        {this.state.showStateModal && (
          <div className="adm-modal-overlay">
            <div className="adm-modal-panel">
              <div className="adm-modal-header">
                <h5>
                  <i className={`fas fa-${this.state.stateFormId ? "edit" : "plus-circle"}`} style={{ color: "var(--adm-primary)" }}></i>
                  {this.state.stateFormId ? "Edit State" : "Add State"}
                </h5>
                <button type="button" className="adm-close-btn" onClick={this.closeStateModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={this.saveState}>
                <div className="adm-modal-body">
                  <div className="adm-filter-group">
                    <label>State Name</label>
                    <input
                      type="text" required
                      value={this.state.stateFormName}
                      onChange={e => this.setState({ stateFormName: e.target.value })}
                      placeholder="Enter state name"
                    />
                  </div>
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-outline" onClick={this.closeStateModal}>Cancel</button>
                  <button type="submit" className="adm-btn adm-btn-primary">
                    <i className="fas fa-save"></i>Save State
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* City Modal */}
        {this.state.showCityModal && (
          <div className="adm-modal-overlay">
            <div className="adm-modal-panel">
              <div className="adm-modal-header">
                <h5>
                  <i className={`fas fa-${this.state.cityFormId ? "edit" : "plus-circle"}`} style={{ color: "var(--adm-primary)" }}></i>
                  {this.state.cityFormId ? "Edit City" : "Add City"}
                </h5>
                <button type="button" className="adm-close-btn" onClick={this.closeCityModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={this.saveCity}>
                <div className="adm-modal-body">
                  <div className="adm-filter-group">
                    <label>City Name</label>
                    <input
                      type="text" required
                      value={this.state.cityFormName}
                      onChange={e => this.setState({ cityFormName: e.target.value })}
                      placeholder="Enter city name"
                    />
                  </div>
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-outline" onClick={this.closeCityModal}>Cancel</button>
                  <button type="submit" className="adm-btn adm-btn-primary">
                    <i className="fas fa-save"></i>Save City
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminLocationsPage;
